import React, { useState } from 'react';
import { CircleCheck } from 'lucide-react';

import { cn } from '../../../../lib/utils';

// "Your part" (Nic 2026-10-27): the tasks only the client can do (VA.gov,
// removing an old POA) as separate raised cards in one band at the top of
// the Right now card, so they read apart from each other and from the
// documents list below. The current task (the first not yet said done) shows
// its steps and buttons; the others collapse to a header and "See the
// steps". A task they already tapped I've done it on shrinks to one line.

export const PRIMARY =
  'inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white no-underline outline-none hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-50 dark:!bg-white dark:!text-slate-900';
export const SECONDARY =
  'inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-white px-5 text-sm font-semibold text-slate-700 no-underline outline-none ring-1 ring-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-50 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10';

// The numbered steps inside an open task, one look for every task.
export const Steps = ({ steps }) => (
  <ol className="mb-4 list-none space-y-2 pl-0 text-sm text-slate-700 dark:text-slate-200">
    {steps.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 dark:!bg-white/10 dark:text-slate-300">
          {i + 1}
        </span>
        <span className="pt-0.5">{t}</span>
      </li>
    ))}
  </ol>
);

const TONE = {
  amber: { rail: 'bg-amber-500', tile: 'bg-amber-500 text-white' },
  violet: { rail: 'bg-violet-600', tile: 'bg-violet-600 text-white' },
};

// todo: theirs to do. waiting: they said it's done, their specialist
// confirms. sentBack: their specialist checked and it wasn't done yet.
const CHIP = {
  todo: { text: 'To do', cls: 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300' },
  waiting: { text: 'Waiting on your specialist', cls: 'bg-sky-100 text-sky-700 dark:!bg-sky-500/20 dark:text-sky-200' },
  sentBack: { text: 'Not done yet', cls: 'bg-amber-100 text-amber-800 dark:!bg-amber-500/20 dark:text-amber-200' },
};

// The band. `toDo` is how many tasks they haven't said done yet.
export const YourPart = ({ toDo, children }) => (
  <div className="bg-slate-50 px-[20px] pb-5 pt-4 dark:!bg-white/[0.03] sm:px-[26px]" data-testid="your-part">
    <div className="flex items-baseline gap-2 pb-3">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Your part</span>
      <span
        className={cn(
          'rounded-full px-1.5 text-[11px] font-bold',
          toDo ? 'bg-slate-200/70 text-slate-600 dark:!bg-white/10 dark:text-slate-300' : 'bg-sky-100 text-sky-700 dark:!bg-sky-500/20 dark:text-sky-200',
        )}
      >
        {toDo ? `${toDo} to do` : 'Waiting on your specialist'}
      </span>
    </div>
    <div className="flex flex-col gap-3">{children}</div>
  </div>
);

// One task.
//   n        its number in the band
//   tone     amber | violet (the rail and icon tile)
//   status   todo | waiting | sentBack (the chip)
//   note     a short amber line under the title (sent back / not set up yet)
//   footer   a quiet line at the bottom (a support line, a how-to)
//   current  open by default; any other task opens from "See the steps"
//   done     what they said (the slim row instead of steps and buttons)
//   aside    a quiet link beside the done row
//   children the steps and buttons
export const TaskCard = ({ n, tone, icon: Icon, title, why, status, note, footer, current, done, aside, children, testId }) => {
  const [opened, setOpened] = useState(false);
  const open = !done && (current || opened);
  const chip = CHIP[status] || CHIP.todo;
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] ring-1 ring-slate-200/80 dark:!bg-surface-card dark:ring-white/10"
      data-testid={testId}
    >
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', TONE[tone]?.rail)} />
      <div className="py-4 pl-5 pr-4 sm:pl-6 sm:pr-5">
        <div className="flex items-start gap-3">
          <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-xl', TONE[tone]?.tile)}>
            <Icon size={19} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white dark:!bg-white dark:!text-slate-900">
                {n}
              </span>
              <h3 className="mb-0 text-base font-bold leading-snug text-slate-900 dark:text-white">{title}</h3>
              <span className={cn('ml-auto whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold', chip.cls)}>
                {chip.text}
              </span>
            </div>
            <p className="mb-0 mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{why}</p>
            {note ? <p className="mb-0 mt-1.5 text-sm font-medium text-amber-700 dark:text-amber-300">{note}</p> : null}
          </div>
        </div>
        {done ? (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              <CircleCheck size={17} className="shrink-0" /> {done}
            </div>
            {aside}
          </div>
        ) : open ? (
          <div className="mt-4">{children}</div>
        ) : (
          <button
            type="button"
            onClick={() => setOpened(true)}
            className="mt-2 text-sm font-semibold text-slate-600 underline underline-offset-2 outline-none hover:text-slate-900 focus:outline-none dark:text-slate-300 dark:hover:text-white"
          >
            See the steps
          </button>
        )}
        {footer && (open || done) ? <p className="mb-0 mt-3 text-xs text-slate-500 dark:text-slate-400">{footer}</p> : null}
      </div>
    </div>
  );
};
